import { type ShippingOrderInfo } from '../../../../types/ai';
import { type OrderTableRow } from '../../OrderTable/OrderTable';
import type { CustomerDetailsLike, CustomerOrderLike } from '../types';

type OrderContext = Record<string, unknown> | null | undefined;

export const readCustomerDetails = (orderContext: OrderContext): CustomerDetailsLike | undefined =>
  (orderContext as { customerDetails?: CustomerDetailsLike } | null | undefined)?.customerDetails;

export const hasCustomerOrderHistory = (orderContext: OrderContext): boolean => {
  const customerDetails = readCustomerDetails(orderContext);
  if (!customerDetails || typeof customerDetails !== 'object') return false;

  const lists = [customerDetails.orders, customerDetails.recentOrders, customerDetails.allOrders];
  return (
    lists.some((list) => Array.isArray(list) && list.length > 0) ||
    (typeof customerDetails.ordersCount === 'number' && customerDetails.ordersCount > 0)
  );
};

export const readShippingInfo = (orderContext: OrderContext): ShippingOrderInfo | null => {
  if (!orderContext || typeof orderContext !== 'object') return null;
  return 'hasShipping' in orderContext ? (orderContext as unknown as ShippingOrderInfo) : null;
};

export function extractOrderRowsForTable(
  orderContext: Record<string, unknown> | null | undefined,
): {
  rows: OrderTableRow[];
  label: string;
} {
  const customerDetails = readCustomerDetails(orderContext);
  const orders = Array.isArray(customerDetails?.orders) ? customerDetails.orders : [];
  if (orders.length === 0) return { rows: [], label: 'Produkter i din senaste order' };

  const normalizeOrderRef = (value: unknown): string | null => {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return null;
  };

  const readOrderRef = (order: CustomerOrderLike | undefined): string | null =>
    normalizeOrderRef(order?.orderNumber) ??
    normalizeOrderRef(order?.number) ??
    normalizeOrderRef(order?.orderId) ??
    normalizeOrderRef(order?.id);

  const referencedOrder = normalizeOrderRef((orderContext as any)?.orderNumber);
  const orderContextCurrency =
    typeof (orderContext as any)?.currency === 'string'
      ? ((orderContext as any).currency as string)
      : undefined;

  const ordersWithDate = orders.filter((o) => typeof o?.createdAt === 'string' && o.createdAt);
  const latestOrder = ordersWithDate.length
    ? [...ordersWithDate].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0]
    : orders[0];

  const selectedOrder = referencedOrder
    ? (orders.find((order) => readOrderRef(order) === referencedOrder) ?? latestOrder)
    : latestOrder;

  const rows = Array.isArray(selectedOrder?.rows) ? selectedOrder.rows : [];
  const fallbackCurrency = selectedOrder?.currency ?? orderContextCurrency;

  const selectedOrderRef = readOrderRef(selectedOrder);
  const label = referencedOrder
    ? `Produkter i order ${referencedOrder}`
    : selectedOrderRef
      ? `Produkter i din senaste order (${selectedOrderRef})`
      : 'Produkter i din senaste order';

  const tableRows: OrderTableRow[] = rows.map((row, i) => {
    const product = row?.product;
    const nameValue = Array.isArray(product?.name)
      ? product?.name?.[0]?.value
      : typeof product?.name === 'string'
        ? product.name
        : undefined;
    const brandValue =
      typeof product?.brand === 'string' ? product.brand : (product?.brand as any)?.name;
    const priceValue = row?.unitPrice?.tax?.priceIncludingTax;
    const currencyValue =
      row?.unitPrice?.tax?.currency ?? row?.unitPrice?.currency ?? fallbackCurrency;
    const urlValue = product?.productUrl ?? product?.url;

    return {
      index: i + 1,
      articleNumber: typeof product?.id === 'string' ? product.id : undefined,
      name: ((nameValue ?? 'Produkt') as string).trim(),
      brand: typeof brandValue === 'string' ? brandValue : undefined,
      imageUrl: typeof product?.imageUrl === 'string' ? product.imageUrl : undefined,
      price: typeof priceValue === 'number' ? priceValue : undefined,
      currency: typeof currencyValue === 'string' ? currencyValue : undefined,
      quantity: typeof row?.quantity === 'number' ? row.quantity : undefined,
      url: typeof urlValue === 'string' ? urlValue : undefined,
    };
  });

  return { rows: tableRows, label };
}
